import { TweenLite } from 'gsap';
import produce from 'immer';
import { round, find, uniqueId } from 'lodash';
import React, { useContext, useRef, useState } from 'react';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import styled from 'styled-components/macro';
import { useOnChange } from '../../../../helpers/hooks/useOnChange';
import { useOnMount } from '../../../../helpers/hooks/useOnMount';
import { assertNotNull } from '../../../../helpers/nullHelpers';
import { Alert } from '../../../../shared/Alert';
import { FormContext } from '../../../../shared/form/Form';
import { FormFieldRow } from '../../../../shared/form/FormFieldRow';
import { spacing01, spacing02, spacing03, spacing06 } from '../../../../styling/spacing';
import { DesignMaterialsCalculationsRequest } from '../../../designs/design-form/calculations/materials/designMaterialsCalculationsResponse';
import { ProjectContext } from '../../../projects/project-section/ProjectContext';
import { AnimateInContext } from '../AnimateInContext';
import { DeleteAndDuplicateButtons, IconButton } from '../DeleteAndDuplicateButtons';
import { DeleteConfirmation } from '../DeleteConfirmation';
import { DragHandle } from '../drag-and-drop/DragHandle';
import { FloorCovering } from '../GetDataForQuoteOrDesignForm';
import { ManifoldFormModel } from '../manifolds/manifoldFormModel';
import { QuoteOrDesignFormContext } from '../QuoteOrDesignFormContext';
import { roomFieldName } from '../quoteOrDesignFormFieldNameHelpers';
import { SectionHeader } from '../SectionHeader';
import { UnitFieldContext } from '../units/UnitFieldContext';
import { UnitFormModel } from '../units/unitFormModel';
import { defaultRoomAirChangeRatesByRoomType } from './heatLoss/roomHeatLossDefaults';
import { calculateNumberOfPortsForFloorConstruction, calculatePipesAndPortsForRoom } from './roomCalculations';
import { DesignRoomFields } from './roomFields/designRoomFields/DesignRoomFields';
import { QuoteRoomFields } from './roomFields/QuoteRoomFields';
import { RoomFormModel } from './roomFormModel';
import { RoomAutocompleteItem, RoomType, roomTypeTemperatureRecord } from './roomTypes';
import { getDefaultMaximumTemperatureInDegreesCelsius, getDefaultScreedDetails } from './designRoomDetailsFormModel';

type RoomComponentProps = {
  unit: UnitFormModel;
  unitIndex: number;
  manifoldIndex: number;
  manifold: ManifoldFormModel;
  roomIndex: number;
  room: RoomFormModel;
  dragHandleProps: DraggableProvidedDragHandleProps | null;
  deleteRoom: () => void;
  duplicateRoom: () => void;
  designMaterialsCalculationsRequest?: DesignMaterialsCalculationsRequest;
};

export const RoomFields = ({
  unitIndex,
  unit,
  manifoldIndex,
  manifold,
  roomIndex,
  room,
  dragHandleProps,
  deleteRoom,
  duplicateRoom,
  designMaterialsCalculationsRequest,
}: RoomComponentProps) => {
  const formContext = assertNotNull(useContext(QuoteOrDesignFormContext));
  const { setFieldValue } = formContext.formProps;
  const { formType } = formContext;

  const unitFieldContext = assertNotNull(useContext(UnitFieldContext));

  const { designCalculations } = unitFieldContext;

  const unitDesignCalculationsResponse = designCalculations && designCalculations.response;

  const isInReadOnlyMode = useContext(FormContext).isDisabled;

  const { project } = useContext(ProjectContext);
  const projectBuildType = project ? project.buildTypeCode : 'NewBuild';

  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const onClickDuplicate = () => {
    duplicateRoom();
  };

  const container = useRef<HTMLDivElement | null>(null);
  const animateInContext = useContext(AnimateInContext);

  const floorCoveringName: string | null = room.heatLossDetails?.floorCoveringName ?? null;
  const floorCovering: FloorCovering | undefined =
    floorCoveringName == null ? undefined : formContext.referenceData.floorCoveringsByName[floorCoveringName];

  useOnMount(() => {
    if (!animateInContext.hasAnimated(room.id)) {
      if (container.current != null) {
        TweenLite.set(container.current, { opacity: 1, backgroundColor: 'transparent' });
        TweenLite.from(container.current, 2, { opacity: 0, backgroundColor: 'white' });
      }
      animateInContext.setAnimated(room.id);
    }
  });

  const handleSelectionOfRoomType = (selectedType: RoomType) => {
    const defaultTemperature = roomTypeTemperatureRecord[selectedType];

    const roomHeatLossDetailsFieldName = (manifoldIndex: number, roomIndex: number, fieldName: string) =>
      `units.${unitIndex}.manifolds.${manifoldIndex}.rooms.${roomIndex}.heatLossDetails.${fieldName}`;

    setFieldValue(roomFieldName(unitIndex, manifoldIndex, roomIndex, 'roomTemperature'), `${defaultTemperature}`);

    if (room.thermostatTypeCode !== 'Shared') {
      const numberOfWetSensors =
        selectedType === 'Bathroom' &&
        ((room.floorConstruction && !room.floorConstruction.isHighOutput) || room.thermostatTypeCode !== 'Dial')
          ? 1
          : 0;

      setFieldValue(roomFieldName(unitIndex, manifoldIndex, roomIndex, 'numberOfWetSensors'), `${numberOfWetSensors}`);
    }

    if (
      room.designRoomDetails != null &&
      room.designRoomDetails.maximumTemperatureInDegreesCelsius ===
        getDefaultMaximumTemperatureInDegreesCelsius(room.roomType, floorCovering)
    ) {
      setFieldValue(
        roomFieldName(unitIndex, manifoldIndex, roomIndex, 'designRoomDetails.maximumTemperatureInDegreesCelsius'),
        getDefaultMaximumTemperatureInDegreesCelsius(selectedType, floorCovering),
      );
    }

    if (room.heatLossDetails != null) {
      const airChangeRateDetails = defaultRoomAirChangeRatesByRoomType[selectedType][projectBuildType];

      if (
        room.roomType == null ||
        room.heatLossDetails.numberOfAirChangesPerHour ===
          defaultRoomAirChangeRatesByRoomType[room.roomType][projectBuildType].NumberOfAirChangesPerHour.toString()
      ) {
        setFieldValue(
          roomHeatLossDetailsFieldName(manifoldIndex, roomIndex, 'numberOfAirChangesPerHour'),
          airChangeRateDetails.NumberOfAirChangesPerHour.toString(),
        );
      }

      if (
        room.roomType == null ||
        room.heatLossDetails.mechanicalVentilationHeatRecoveryEfficiencyPercentage ===
          defaultRoomAirChangeRatesByRoomType[room.roomType][projectBuildType].mvhrEfficiencyInPercentage.toString()
      ) {
        setFieldValue(
          roomHeatLossDetailsFieldName(
            manifoldIndex,
            roomIndex,
            'mechanicalVentilationHeatRecoveryEfficiencyPercentage',
          ),
          airChangeRateDetails.mvhrEfficiencyInPercentage.toString(),
        );
      }
    }
  };

  const handleSelectionOfRoomName = (selectedItem: RoomAutocompleteItem) => {
    setFieldValue(roomFieldName(unitIndex, manifoldIndex, roomIndex, 'roomType'), selectedItem.roomType);
    handleSelectionOfRoomType(selectedItem.roomType);
  };

  useOnChange(() => {
    if (room.floorConstruction == null) {
      return;
    }

    let numberOfWetSensors = 0;

    if (room.thermostatTypeCode !== 'Shared') {
      if (!room.floorConstruction.isHighOutput && !floorCovering?.isSensitive) {
        setFieldValue(roomFieldName(unitIndex, manifoldIndex, roomIndex, 'numberOfFloorSensors'), '0');
      }

      numberOfWetSensors =
        room.roomType === 'Bathroom' && (!room.floorConstruction.isHighOutput || room.thermostatTypeCode !== 'Dial')
          ? 1
          : 0;
    }

    setFieldValue(roomFieldName(unitIndex, manifoldIndex, roomIndex, 'numberOfWetSensors'), `${numberOfWetSensors}`);

    if (room.floorConstruction.isSkirtingHeating) {
      setFieldValue(
        roomFieldName(unitIndex, manifoldIndex, roomIndex, 'percentageHeated'),
        `${room.floorConstruction.percentageHeated}`,
      );
    }

    if (!room.floorConstruction.isSkirtingHeating) {
      setFieldValue(roomFieldName(unitIndex, manifoldIndex, roomIndex, 'perimeterInMetres'), '');
    }

    if (formType === 'Quote') {
      // We update the Number of Ports differently for Designs
      if (room.floorConstruction.isElectricUnderfloorHeating) {
        setFieldValue(roomFieldName(unitIndex, manifoldIndex, roomIndex, 'numberOfPorts'), '');
      } else {
        const numberOfPorts = calculateNumberOfPortsForFloorConstruction(
          room.floorConstruction,
          room.areaInMetresSquared,
          projectBuildType,
        );
        if (numberOfPorts != null) {
          setFieldValue(roomFieldName(unitIndex, manifoldIndex, roomIndex, 'numberOfPorts'), numberOfPorts.toString());
        }
      }
    }

    const {
      defaultDeckingThermalResistancePerMillimetre,
      defaultDeckingThicknessInMillimetres,
    } = room.floorConstruction;
    if (room.heatLossDetails != null) {
      setFieldValue(
        roomFieldName(unitIndex, manifoldIndex, roomIndex, 'heatLossDetails.deckingThermalResistancePerMillimetre'),
        defaultDeckingThermalResistancePerMillimetre == null
          ? '0'
          : defaultDeckingThermalResistancePerMillimetre.toString(),
      );

      setFieldValue(
        roomFieldName(unitIndex, manifoldIndex, roomIndex, 'heatLossDetails.deckingThicknessInMillimetres'),
        defaultDeckingThicknessInMillimetres == null ? '0' : defaultDeckingThicknessInMillimetres.toString(),
      );
    }

    if (room.designRoomDetails != null) {
      const screedDetails = getDefaultScreedDetails(room.floorConstruction);

      setFieldValue(
        roomFieldName(unitIndex, manifoldIndex, roomIndex, 'designRoomDetails.screedDetails'),
        screedDetails,
      );

      if (!room.floorConstruction.requiresPeripheralArea) {
        setFieldValue(
          roomFieldName(unitIndex, manifoldIndex, roomIndex, 'designRoomDetails.peripheralAreaInMetresSquared'),
          '',
        );
      }
      if (!room.floorConstruction.requiresTurnPanelQuantity) {
        setFieldValue(roomFieldName(unitIndex, manifoldIndex, roomIndex, 'designRoomDetails.turnPanelQuantity'), '');
      }
      if (!room.floorConstruction.requiresStraightPanelQuantity) {
        setFieldValue(
          roomFieldName(unitIndex, manifoldIndex, roomIndex, 'designRoomDetails.straightPanelQuantity'),
          '',
        );
      }
      if (!room.floorConstruction.requiresPlateArea) {
        setFieldValue(
          roomFieldName(unitIndex, manifoldIndex, roomIndex, 'designRoomDetails.plateAreaInMetresSquared'),
          '',
        );
      }
    }
  }, [room.floorConstruction]);

  const disableNumberOfPortsAndPipesPerPortCalculation =
    manifold.designManifoldDetails?.disableNumberOfPortsAndPipesPerPortCalculation ?? false;

  useOnChange(() => {
    if (room.floorConstruction != null) {
      const numberOfPorts = calculateNumberOfPortsForFloorConstruction(
        room.floorConstruction,
        room.areaInMetresSquared,
        projectBuildType,
      );
      if (numberOfPorts != null && (formType === 'Quote' || !disableNumberOfPortsAndPipesPerPortCalculation)) {
        setFieldValue(roomFieldName(unitIndex, manifoldIndex, roomIndex, 'numberOfPorts'), numberOfPorts.toString());
      }
    }
  }, [room.areaInMetresSquared, disableNumberOfPortsAndPipesPerPortCalculation]);

  useOnChange(() => {
    let numberOfThermostats = 0;
    let numberOfWetSensors = 0;

    if (room.thermostatTypeCode !== 'Shared') {
      numberOfThermostats = 1;
      numberOfWetSensors =
        room.roomType === 'Bathroom' &&
        ((room.floorConstruction && !room.floorConstruction.isHighOutput) || room.thermostatTypeCode !== 'Dial')
          ? 1
          : 0;

      if (room.floorConstruction && !room.floorConstruction.isHighOutput && !floorCovering?.isSensitive) {
        setFieldValue(roomFieldName(unitIndex, manifoldIndex, roomIndex, 'numberOfFloorSensors'), '0');
      }

      setFieldValue(roomFieldName(unitIndex, manifoldIndex, roomIndex, 'thermostatSharedWithId'), null);
    } else {
      setFieldValue(roomFieldName(unitIndex, manifoldIndex, roomIndex, 'numberOfFloorSensors'), '0');
    }

    setFieldValue(roomFieldName(unitIndex, manifoldIndex, roomIndex, 'numberOfThermostats'), `${numberOfThermostats}`);
    setFieldValue(roomFieldName(unitIndex, manifoldIndex, roomIndex, 'numberOfWetSensors'), `${numberOfWetSensors}`);
  }, [room.thermostatTypeCode]);

  const roomDesignCalculationsResponse =
    (unitDesignCalculationsResponse &&
      find(
        unitDesignCalculationsResponse.rooms,
        r => r.manifoldIndex === manifoldIndex && r.roomIndex === roomIndex,
      )) ||
    null;

  const overrideTotalHeatLossInWatts =
    room.heatLossDetails != null && room.heatLossDetails.overrideTotalHeatLossInWatts;

  useOnChange(() => {
    if (overrideTotalHeatLossInWatts) {
      setFieldValue(
        roomFieldName(unitIndex, manifoldIndex, roomIndex, 'heatLossDetails.userEnteredTotalHeatLossInWatts'),
        (roomDesignCalculationsResponse &&
          round(roomDesignCalculationsResponse.heatLoss.totalHeatLossInWatts, 2).toString()) ||
          '',
      );
    } else {
      setFieldValue(
        roomFieldName(unitIndex, manifoldIndex, roomIndex, 'heatLossDetails.userEnteredTotalHeatLossInWatts'),
        null,
      );
    }
  }, [overrideTotalHeatLossInWatts]);

  const roomAreaInMetresSquared = room.areaInMetresSquared;
  const voidAreaInMetresSquared = room.designRoomDetails?.voidAreaInMetresSquared ?? '';
  const unheatedAreaInMetresSquared = room.designRoomDetails?.unheatedAreaInMetresSquared ?? '';
  const floorConstruction = room.floorConstruction;
  const manifoldDistanceInMetres = room.designRoomDetails?.manifoldDistanceInMetres ?? '';
  const verticalRunbackInMetres = room.designRoomDetails?.verticalRunbackInMetres ?? '';

  useOnChange(() => {
    if (formType === 'Design') {
      // We update the Number of Ports differently for Quotes
      if (floorConstruction?.isSkirtingHeating) {
        if (!disableNumberOfPortsAndPipesPerPortCalculation) {
          setFieldValue(roomFieldName(unitIndex, manifoldIndex, roomIndex, 'numberOfPorts'), '1');
          setFieldValue(roomFieldName(unitIndex, manifoldIndex, roomIndex, 'designRoomDetails.pipesPerPort'), '1');
        }

        setFieldValue(
          roomFieldName(unitIndex, manifoldIndex, roomIndex, 'designRoomDetails.estimatedLengthOfEachPipeInMetres'),
          '',
        );
      } else {
        const calculationResult = calculatePipesAndPortsForRoom({
          roomAreaInMetresSquared,
          voidAreaInMetresSquared,
          unheatedAreaInMetresSquared,
          floorConstruction,
          projectBuildType,
          manifoldDistanceInMetres,
          verticalRunbackInMetres,
        });

        if (!disableNumberOfPortsAndPipesPerPortCalculation) {
          setFieldValue(
            roomFieldName(unitIndex, manifoldIndex, roomIndex, 'numberOfPorts'),
            calculationResult?.numberOfPorts.toString() ?? '',
          );
          setFieldValue(
            roomFieldName(unitIndex, manifoldIndex, roomIndex, 'designRoomDetails.pipesPerPort'),
            calculationResult?.pipesPerPort.toString() ?? '',
          );
        }

        setFieldValue(
          roomFieldName(unitIndex, manifoldIndex, roomIndex, 'designRoomDetails.estimatedLengthOfEachPipeInMetres'),
          calculationResult?.estimatedLengthOfEachPipeInMetres ?? null,
        );
      }
    }
  }, [
    roomAreaInMetresSquared,
    voidAreaInMetresSquared,
    unheatedAreaInMetresSquared,
    floorConstruction,
    manifoldDistanceInMetres,
    verticalRunbackInMetres,
    disableNumberOfPortsAndPipesPerPortCalculation,
  ]);

  return (
    <div ref={container} style={{ padding: `${spacing02}` }}>
      <RoomSectionHeader>
        <RoomDragHandle dragHandleProps={dragHandleProps} disabled={isInReadOnlyMode} />
        {!isInReadOnlyMode && (
          <RoomDeleteAndDuplicateButtons
            onClickDelete={() => setIsConfirmingDelete(true)}
            onClickDuplicate={onClickDuplicate}
            deleteButtonTestId={roomDeleteButtonTestId(unitIndex, manifoldIndex, roomIndex)}
          >
            <></>
          </RoomDeleteAndDuplicateButtons>
        )}
        {formType === 'Quote' ? (
          <QuoteRoomFields
            unitIndex={unitIndex}
            manifoldIndex={manifoldIndex}
            manifold={manifold}
            roomIndex={roomIndex}
            room={room}
            handleSelectionOfRoomName={handleSelectionOfRoomName}
            handleSelectionOfRoomType={handleSelectionOfRoomType}
          />
        ) : (
          <DesignRoomFieldsContainer>
            <FormFieldRow>
              <DesignRoomFields
                unitViewType={unit.designViewType || 'Basic'}
                unitIndex={unitIndex}
                manifoldIndex={manifoldIndex}
                manifold={manifold}
                roomIndex={roomIndex}
                room={room}
                handleSelectionOfRoomName={handleSelectionOfRoomName}
                handleSelectionOfRoomType={handleSelectionOfRoomType}
                roomDesignCalculationsResponse={roomDesignCalculationsResponse}
                designMaterialsCalculationsRequest={designMaterialsCalculationsRequest}
              />
            </FormFieldRow>
            {roomDesignCalculationsResponse != null &&
              roomDesignCalculationsResponse.heatOutput != null &&
              roomDesignCalculationsResponse.heatOutput.shortfallInWatts > 0 && (
                <RoomAlert type="warning" body="This Room has a Heat Output shortfall" />
              )}
          </DesignRoomFieldsContainer>
        )}
      </RoomSectionHeader>
      <DeleteConfirmationWrapper>
        <DeleteConfirmation
          isConfirmingDelete={isConfirmingDelete}
          setIsConfirmingDelete={setIsConfirmingDelete}
          deleteItem={deleteRoom}
          deleteButtonTestId={roomConfirmDeleteButtonTestId(unitIndex, manifoldIndex, roomIndex)}
        />
      </DeleteConfirmationWrapper>
    </div>
  );
};

export const roomDeleteButtonTestId = (unitIndex: number, manifoldIndex: number, roomIndex: number) =>
  `unit-${unitIndex}-manifold-${manifoldIndex}-room-${roomIndex}-delete-button`;

export const roomConfirmDeleteButtonTestId = (unitIndex: number, manifoldIndex: number, roomIndex: number) =>
  `unit-${unitIndex}-manifold-${manifoldIndex}-room-${roomIndex}-confirm-delete-button`;

export const createDuplicateRoom = (room: RoomFormModel) => {
  return produce(room, draftRoom => {
    draftRoom.roomId = null;
    draftRoom.id = uniqueId('room');
  });
};

const RoomSectionHeader = styled(SectionHeader)`
  margin-bottom: 0;
`;

const RoomDragHandle = styled(DragHandle)`
  margin-right: ${spacing03};
`;

const RoomDeleteAndDuplicateButtons = styled(DeleteAndDuplicateButtons)`
  min-width: 40px;
  width: 40px;
  margin-left: 0;
  margin-right: ${spacing02};

  ${IconButton} {
    height: 36px;
    width: 36px;
    padding: ${spacing01};
    margin: 0 0 ${spacing01};
  }
`;

const DeleteConfirmationWrapper = styled.div`
  margin-left: ${spacing06};
`;

const DesignRoomFieldsContainer = styled.div`
  flex: 1;

  ${FormFieldRow} {
    margin: 0;
  }
`;

const RoomAlert = styled(Alert)`
  margin-top: ${spacing03};
`;
